/**
 * Hold to peek.
 *
 * The arrow answers "which way to one thing". Sometimes what you want is the *shape* of the
 * group — who is behind you, who is miles away, whether the meeting point is on the far side of
 * everybody. That is a map-shaped question, and the product's answer to map-shaped questions is
 * that they are allowed to exist as long as they never replace the arrow.
 *
 * So: press and hold, see everybody laid out around you, let go and it is an arrow again. No
 * tiles, no streets, no zoom — a radar, not a map. The distances are compressed logarithmically
 * because a group spread over four hundred metres and a group spread over forty both have to be
 * readable, and a linear scale can only do one of them.
 */

import { compassPoint, formatDistance, type ParticipantId } from '@beeline/shared';

import { el } from '../lib/dom';
import type { MemberGeometry } from '../lib/geometry';

export interface PeekEntry {
  id: ParticipantId | 'rally';
  label: string;
  distanceMetres: number | null;
  /** Bearing minus heading. Without a compass this falls back to true-north bearing. */
  relative: number | null;
  bearing: number | null;
  stale: boolean;
}

export interface Peek {
  element: HTMLElement;
  /** Attach press-and-hold to something — the stage, usually. */
  bindTo: (target: HTMLElement) => () => void;
  update: (entries: PeekEntry[], northUp: boolean) => void;
  destroy: () => void;
}

/** Hold this long before it opens, so an ordinary tap is still an ordinary tap. */
const HOLD_MS = 280;

/**
 * Where a blip sits, from 0 (on top of you) to 1 (at the rim).
 *
 * Logarithmic: the first fifty metres get half the radius, and everything past four hundred is
 * pinned near the edge. Somebody at 12 m and somebody at 300 m are both legible, which a linear
 * scale cannot manage.
 */
export function peekRadius(distanceMetres: number | null): number {
  if (distanceMetres === null || distanceMetres <= 0) return 0;
  const scaled = Math.log10(1 + distanceMetres / 8) / Math.log10(1 + 400 / 8);
  return Math.min(1, Math.max(0.06, scaled));
}

export function createPeek(): Peek {
  const field = el('div', { class: 'peek__field' });
  const caption = el('p', { class: 'peek__caption' });

  const element = el(
    'div',
    { class: 'peek', hidden: true, 'aria-hidden': 'true' },
    el(
      'div',
      { class: 'peek__dial' },
      el('span', { class: 'peek__north', text: 'N' }),
      el('span', { class: 'peek__you' }),
      field,
    ),
    caption,
  );

  const blips = new Map<ParticipantId | 'rally', HTMLElement>();

  const blipFor = (id: ParticipantId | 'rally'): HTMLElement => {
    const existing = blips.get(id);
    if (existing) return existing;

    const blip = el(
      'span',
      { class: `peek__blip${id === 'rally' ? ' peek__blip--rally' : ''}` },
      el('span', { class: 'peek__label' }),
    );
    blips.set(id, blip);
    field.append(blip);
    return blip;
  };

  return {
    element,

    bindTo: (target) => {
      let timer: number | null = null;

      const open = (): void => {
        element.hidden = false;
      };
      const close = (): void => {
        if (timer !== null) window.clearTimeout(timer);
        timer = null;
        element.hidden = true;
      };

      const onDown = (): void => {
        if (timer !== null) window.clearTimeout(timer);
        timer = window.setTimeout(open, HOLD_MS);
      };

      target.addEventListener('pointerdown', onDown);
      target.addEventListener('pointerup', close);
      target.addEventListener('pointercancel', close);
      target.addEventListener('pointerleave', close);

      return () => {
        close();
        target.removeEventListener('pointerdown', onDown);
        target.removeEventListener('pointerup', close);
        target.removeEventListener('pointercancel', close);
        target.removeEventListener('pointerleave', close);
      };
    },

    update: (entries, northUp) => {
      caption.textContent = northUp
        ? 'North is up — no compass on this device'
        : 'Everything around you. Let go to go back.';

      const seen = new Set<ParticipantId | 'rally'>();

      for (const entry of entries) {
        // Without an angle there is nowhere honest to put a blip, so it does not get one.
        const angle = northUp ? entry.bearing : entry.relative;
        if (angle === null) continue;
        seen.add(entry.id);

        const blip = blipFor(entry.id);
        blip.style.setProperty('--peek-angle', `${angle}deg`);
        blip.style.setProperty('--peek-radius', String(peekRadius(entry.distanceMetres)));
        blip.classList.toggle('peek__blip--stale', entry.stale);
        blip.hidden = false;

        const label = blip.firstElementChild;
        if (label) {
          label.textContent =
            entry.distanceMetres === null
              ? entry.label
              : `${entry.label} · ${formatDistance(entry.distanceMetres)}`;
        }
      }

      for (const [id, blip] of blips) {
        if (!seen.has(id)) blip.hidden = true;
      }
    },

    destroy: () => {
      blips.clear();
      element.remove();
    },
  };
}

/** Turn the live screen's geometry into the shape the radar wants. */
export const peekEntriesFrom = (
  everyone: MemberGeometry[],
  rally: { label: string; geometry: MemberGeometry['geometry'] } | null,
): PeekEntry[] => [
  ...(rally
    ? [
        {
          id: 'rally' as const,
          label: rally.label,
          distanceMetres: rally.geometry.distanceMetres,
          relative: rally.geometry.relative,
          bearing: rally.geometry.bearing,
          stale: false,
        },
      ]
    : []),
  ...everyone.map(({ member, geometry }) => ({
    id: member.id,
    label: member.name ?? member.id.toUpperCase(),
    distanceMetres: geometry.distanceMetres,
    relative: geometry.relative,
    bearing: geometry.bearing,
    stale: member.staleness !== 'live',
  })),
];

/** Spoken layout for the no-compass case, so the radar is not the only way to read it. */
export const describePeekEntry = (entry: PeekEntry): string =>
  entry.bearing === null
    ? entry.label
    : `${entry.label}, ${compassPoint(entry.bearing)}, ${
        entry.distanceMetres === null ? 'distance unknown' : formatDistance(entry.distanceMetres)
      }`;
